/* eslint-disable camelcase */
/* eslint-disable react/display-name */
import { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import Cookies from 'js-cookie';
import { useDispatch } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';
import { getAffiliate } from 'utils/affiliateUtils';
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  TextField,
  Typography,
} from '@mui/material';

import CelMaskCustomComponent from 'components/MasksComponents/CelMaskCustomComponent';
import SelectComponent from 'components/SelectComponent';
import SelectGroupComponent from 'components/SelectGroupComponent';
import { ConvertToSelectComponent } from 'components/SelectComponent/ConvertToSelectComponent';

import { saveLead, getAgeRanges, setPriceOpen } from 'utils/lead';
import { sendEvent } from 'services/middleware/eventService';
import { saveState, loadState } from 'utils/localStorage';
import { getCountryFilterById } from 'utils/countryUtils';
import { getCities } from 'utils/cityUtils';
import {
  addLongTermOptions,
  getWeekRange,
  getGroupedMondayOptions,
} from 'utils/date';
import { gtmEventTrigger } from 'utils/gtagUtils';

import {
  emailableRuleChecker,
  generalNameProperties,
  generalRequiredWord,
  generalPhoneProperties,
} from 'utils/useForm/register';

import { AddLead } from 'store/actions/studentActions';

PersonalDataFormComponent.propTypes = {
  age: PropTypes.number,
  buttonText: PropTypes.string,
  cityId: PropTypes.number,
  countries: PropTypes.instanceOf(Array),
  countryId: PropTypes.number,
  courseId: PropTypes.number,
  salesExecutiveCode: PropTypes.number,
  id: PropTypes.string.isRequired,
  internalStore: PropTypes.instanceOf(Object),
  onSubmitFirstStep: PropTypes.instanceOf(Function),
  setIsPremiumPlanUser: PropTypes.bool,
  setMeet: PropTypes.instanceOf(Function).isRequired,
  setStep: PropTypes.instanceOf(Function).isRequired,
  query: PropTypes.instanceOf(Object),
};

export default function PersonalDataFormComponent({
  age = 0,
  buttonText = 'Avançar',
  cityId = 0,
  countries = [],
  countryId = 0,
  courseId = 0,
  salesExecutiveCode = null,
  id,
  internalStore = {},
  onSubmitFirstStep = () => {},
  setIsPremiumPlanUser = false,
  setMeet,
  setStep,
  query = {},
}) {
  const ages = getAgeRanges();
  const affiliate = getAffiliate();
  const cookies = {
    usertokenHubSpot: Cookies.get('hubspotutk'),
  };
  const courseTypes = internalStore?.courseTypes || [];
  const languageFilters = internalStore?.languageFilters || [];
  const weekRange = getWeekRange();
  const dispatch = useDispatch();
  const {
    formState: { errors },
    handleSubmit,
    register,
    setError,
    setValue,
    control,
  } = useForm({
    mode: 'onBlur',
    defaultValues: {
      cityId,
      countryId,
      courseId,
      weekQuantity: 0,
    },
  });
  const [cities, setCities] = useState(getCities(languageFilters, countryId));
  const [emailChecker, setEmailChecker] = useState('');
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    age,
    cityId,
    countryId,
    courseId,
    date: 0,
    phone: '+55',
    weekQuantity: 0,
  });

  const formChange = (ref, value) => {
    setValue(ref, value);
    setForm({ ...form, [ref]: value });
  };

  const formChangeCountry = (value) => {
    formChange('countryId', value);

    setCities(getCities(languageFilters, value));
  };

  const getSelectedCity = (formData) => {
    let selectedCity = { city: 'Selecione' };

    const selectedCountry = getCountryFilterById(
      languageFilters,
      formData.countryId
    );

    if (selectedCountry) {
      const filterCity = selectedCountry.schoolsByCity.filter(
        (c) => c.cityId === formData.cityId
      )[0];

      if (filterCity) selectedCity = filterCity;
    }

    return selectedCity;
  };

  const setMeetingByCountry = useCallback(
    (countryIdentifier) => {
      const [selectedCountry] = countries.filter(
        (c) => c.id === countryIdentifier
      );

      if (selectedCountry) setMeet(selectedCountry.link);
    },
    [countries, setMeet]
  );

  useEffect(() => {
    setMeetingByCountry(countryId);
  }, [countryId, setMeetingByCountry]);

  const onHandleSubmit = async (formData) => {
    setLoading(true);
    const {
      age: _age,
      date: dt_embarque,
      email,
      firstName: firstname,
      phone,
      weekQuantity,
    } = formData;

    const [courseSelected] = courseTypes.filter(
      (c) => c.id === formData.courseId
    );
    const countrySelected = getCountryFilterById(
      languageFilters,
      formData.countryId
    );
    const citySelected = getSelectedCity(formData);

    const student = {
      age: _age,
      channel: 'b2c_abroad',
      crmIntegrated: true,
      email,
      firstname,
      phone,
    };

    const currentState = loadState();

    const lead = {
      acquireUrl: window.location.href,
      affiliateCode: affiliate.code,
      affiliateProvider: affiliate.provider,
      age: _age,
      channel: 'b2c_abroad',
      city_id: citySelected.cityId,
      city_travel: citySelected.city,
      country_id: countrySelected.countryId,
      country_travel: countrySelected.country,
      course_type:
        courseSelected === undefined
          ? courseTypes[0].type
          : courseSelected.type,
      crmIntegrated: true,
      dt_embarque,
      email,
      salesExecutiveCode: +salesExecutiveCode,
      firstname,
      phone,
      referralCode: currentState ? currentState.referralCode : undefined,
      usertokenHubSpot: cookies?.usertokenHubSpot,
      weekQuantity: +weekQuantity,
      affiliateId: query?.affiliate,
      campaign: query?.utm_campaign,
      content: query?.utm_content,
      device: query?.utm_device,
      medium: query?.utm_medium,
      rec: query?.rec,
      siteSourceName: query?.site_source_name,
      source: query?.utm_source,
      term: query?.utm_term,
    };

    await saveLead(student, lead).then((res) => {
      setLoading(false);

      setPriceOpen(lead, false);

      if (res.data.validEmail) {
        lead.step = res?.data?.step || 1;

        AddLead(dispatch, lead);
        saveState({ lead });

        Cookies.set('st-identifier', res.data.studentIdentifier);

        onSubmitFirstStep({ formData, countrySelected, citySelected });
        setStep(res.data.step);
        setIsPremiumPlanUser(res?.data?.isPremiumPlanUser);
        sendEvent(`${id}-${res.data.step}`);
        gtmEventTrigger(`${id}-${res.data.step}`);
      } else {
        setError('email');
        setEmailChecker('E-mail inválido, tente novamente.');
      }
    });
  };

  return (
    <form id={id} onSubmit={handleSubmit(onHandleSubmit)} autoComplete="off">
      <Grid container spacing={2} alignItems="flex-start">
        <Grid item xs={12} sm={6}>
          <TextField
            InputLabelProps={{ shrink: true }}
            error={!!errors?.firstName}
            fullWidth
            helperText={errors?.firstName?.message}
            id="name"
            inputProps={{ maxLength: 40 }}
            variant="standard"
            label={
              <Typography variant="subtitle1" fontWeight="bold">
                Primeiro nome
              </Typography>
            }
            onChange={(e) => formChange('firstName', e.target.value)}
            placeholder="Digite seu primeiro Nome"
            {...register('firstName', generalNameProperties)}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          {ages.length > 0 && (
            <SelectComponent
              change={(value) => formChange('age', value)}
              disableLeft
              disableMargin
              error={!!errors?.age}
              helperText={errors?.age?.message}
              id="age"
              isDuplicateText={false}
              items={ConvertToSelectComponent(ages, 'value', 'text')}
              name="age"
              placeholder="Selecione"
              text={
                <Typography variant="subtitle1" fontWeight="bold">
                  Idade
                </Typography>
              }
              value={form.age}
              variant="standard"
              inputRef={{
                ...register('age', {
                  required: 'Selecione sua idade',
                  min: {
                    value: 1,
                    message: 'Selecione sua idade',
                  },
                }),
              }}
            />
          )}
        </Grid>

        <Grid item xs={12}>
          <Controller
            name="phone"
            control={control}
            rules={{
              validate: generalPhoneProperties.validate,
            }}
            render={({ field }) => (
              <CelMaskCustomComponent
                {...field}
                InputLabelProps={{ shrink: true }}
                error={!!errors?.phone}
                fullWidth
                helperText={errors?.phone?.message}
                id="phone"
                variant="standard"
                label={
                  <Typography variant="subtitle1" fontWeight="bold">
                    Seu Celular
                  </Typography>
                }
                name="phone"
                onChange={(e) => formChange('phone', e)}
                placeholder="Digite seu Celular"
                value={form.phone}
              />
            )}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            InputLabelProps={{ shrink: true }}
            error={!!errors?.email}
            fullWidth
            helperText={errors?.email?.message || emailChecker}
            id="email"
            inputProps={{ maxLength: 100, type: 'email' }}
            variant="standard"
            label={
              <Typography variant="subtitle1" fontWeight="bold">
                Seu e-mail
              </Typography>
            }
            name="email"
            placeholder="Digite seu email@aqui.com"
            {...register('email', {
              ...generalRequiredWord('Email'),
              validate: (value) => emailableRuleChecker(value),
              onChange: (e) => {
                formChange('email', e.target.value.toLowerCase());
                setEmailChecker('');
              },
            })}
          />
        </Grid>

        <Grid item xs={12}>
          {courseTypes.length > 0 && (
            <SelectComponent
              change={(value) => formChange('courseId', parseInt(value, 10))}
              disableLeft
              disableMargin
              id="course"
              isDuplicateText={false}
              items={ConvertToSelectComponent(courseTypes, 'id', 'type')}
              name="courseId"
              text={
                <Typography variant="subtitle1" fontWeight="bold">
                  Seu curso
                </Typography>
              }
              value={form.courseId}
              variant="standard"
              inputRef={{ ...register('courseId') }}
            />
          )}
        </Grid>

        <Grid item xs={6}>
          {countries.length > 0 && (
            <SelectComponent
              change={(value) => {
                formChangeCountry(+value);
                setMeetingByCountry(+value);
              }}
              disableLeft
              disableMargin
              disabled={countryId > 0}
              error={!!errors?.countryId}
              helperText={errors?.countryId?.message}
              id="countryId"
              isDuplicateText={false}
              items={ConvertToSelectComponent(countries, 'id', 'name')}
              name="countryId"
              placeholder="Selecione"
              text={
                <Typography variant="subtitle1" fontWeight="bold">
                  Qual o destino
                </Typography>
              }
              value={form.countryId}
              variant="standard"
              inputRef={{
                ...register('countryId', {
                  min: {
                    value: 1,
                    message: 'Selecione o destino',
                  },
                }),
              }}
            />
          )}
        </Grid>

        <Grid item xs={6}>
          {cities.length > 0 && (
            <SelectComponent
              change={(value) => formChange('cityId', parseInt(value, 10))}
              disableLeft
              disableMargin
              disabled={cityId > 0}
              error={!!errors?.cityId}
              helperText={errors?.cityId?.message}
              id="cityId"
              isDuplicateText={false}
              items={ConvertToSelectComponent(cities, 'id', 'name')}
              name="cityId"
              placeholder="Selecione"
              text={
                <Typography variant="subtitle1" fontWeight="bold">
                  Qual cidade
                </Typography>
              }
              value={form.cityId}
              variant="standard"
              inputRef={{
                ...register('cityId', {
                  min: {
                    value: 1,
                    message: 'Selecione a cidade',
                  },
                }),
              }}
            />
          )}
        </Grid>

        <Grid item xs={6}>
          <SelectGroupComponent
            change={(value) => formChange('date', value)}
            disableMargin
            error={!!errors?.date}
            groups={addLongTermOptions(getGroupedMondayOptions())}
            helperText={errors?.date?.message}
            id="boardDate"
            name="date"
            placeholder="Selecione"
            text={
              <Typography variant="subtitle1" fontWeight="bold">
                Data de início
              </Typography>
            }
            value={form.date}
            variant="standard"
            inputRef={{
              ...register('date', {
                required: 'Selecione uma data',
                min: {
                  value: 1,
                  message: 'Selecione uma data',
                },
              }),
            }}
          />
        </Grid>

        <Grid item xs={6}>
          {weekRange?.length > 0 && (
            <SelectComponent
              change={(value) =>
                formChange('weekQuantity', parseInt(value, 10))
              }
              disableLeft
              disableMargin
              error={!!errors?.weekQuantity}
              helperText={errors?.weekQuantity?.message}
              id="weekQuantity"
              isDuplicateText={false}
              items={weekRange}
              name="weekQuantity"
              placeholder="Selecione"
              text={
                <Typography variant="subtitle1" fontWeight="bold">
                  Semanas
                </Typography>
              }
              value={form.weekQuantity}
              variant="standard"
              inputRef={{
                ...register('weekQuantity', {
                  required: 'Selecione as semanas',
                  min: {
                    value: 1,
                    message: 'Selecione as semanas',
                  },
                }),
              }}
            />
          )}
        </Grid>

        {salesExecutiveCode && (
          <input
            {...register('salesExecutiveCode')}
            type="hidden"
            value={salesExecutiveCode}
          />
        )}

        <Grid item xs={12}>
          <Box position="relative">
            <Button
              className="lead-form"
              color="primary"
              disabled={loading}
              fullWidth
              type="submit"
              variant="contained"
              data-testid="forward-button"
            >
              {buttonText}
            </Button>

            {loading && (
              <CircularProgress
                size={24}
                sx={{
                  left: '50%',
                  marginLeft: '-12px',
                  marginTop: '-12px',
                  position: 'absolute',
                  top: '50%',
                }}
              />
            )}
          </Box>
        </Grid>
      </Grid>
    </form>
  );
}
